import { nextTick, ref } from 'vue';
import useLocale from '../helpers/use-locale';

/**
 * Shared state for accessible reordering. Three ways in, all announced the same way:
 * drag by the handle, tap the move buttons (the WCAG 2.5.7 single-pointer alternative),
 * or focus a row and grab it with space, then move it with the arrow keys.
 *
 * `getRoot` returns the element that contains the rows, so focus can follow a moved
 * item without the caller having to hand out template refs.
 */
const useReorder = (getRoot: () => HTMLElement | null | undefined) => {
  const { t } = useLocale();

  const assistiveText = ref('');

  const announce = (key: string, title: string, index: number, total: number) => {
    assistiveText.value = t(`components.${key}`, { title, position: index + 1, total });
  };

  const announceMove = (title: string, index: number, total: number) =>
    announce('reorder_item_moved', title, index, total);

  const announceGrab = (title: string, index: number, total: number) =>
    announce('reorder_item_grabbed', title, index, total);

  const announceDrop = (title: string, index: number, total: number) =>
    announce('reorder_item_dropped', title, index, total);

  // A grabbed row keeps focus as it travels, so the arrow keys keep landing on the same item.
  const focusRow = (index: number) => {
    nextTick(() => {
      const rows = getRoot()?.querySelectorAll<HTMLElement>('.drag__item');
      rows?.[index]?.focus();
    });
  };

  return { assistiveText, announceMove, announceGrab, announceDrop, focusRow };
};

export default useReorder;
